import React, { Component } from 'react';

import { ButtonIcon } from 'components/button';
import { Card } from 'components/card';
import { Input, InputLabel, Loading } from 'components/cosmos';
import Button from 'components/cosmos/button/button';
import { translate } from 'components/translate';
import HttpCodes from 'configs/HttpCodes';
import { ValidationError } from 'helpers/errors';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import { DatahubEventsService } from 'modules/app/services';
import { TryCreateUrlReadSchema } from 'modules/app/services/datahubEvents/complementaryReading';
import { ContentPageService } from 'modules/content';
import ComplementaryReadingValidation from 'modules/content/components/complementaryReading/complementaryReadingValidation';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';

import { sanitize } from '@hotmart/devsec-toolkit';

import './ComplementaryReading.scss';

@translate()
export default class ComplementaryReading extends Component {
  static propTypes = {
    articles: MobxPropTypes.observableArray,
    onArticleChange: PropTypes.func
  };

  constructor(props) {
    super(props);

    this.state = {
      articleUrl: '',
      articleName: '',
      siteName: '',
      faviconUrl: '',
      loading: false,
      faviconError: false,
      errors: {}
    };
  }

  urlChange = (e) => {
    const articleUrl = e.target.value;

    this.setState({ articleUrl });

    if (!articleUrl) {
      this.setState({ articleName: '', siteName: '' });
    }
  };

  nameChange = (e) => {
    this.setState({ siteName: e.target.value });
  };

  articleChange = (e) => {
    this.setState({ articleName: e.target.value });
  };

  loadReadingData = async () => {
    const { articleUrl, articleName, siteName } = this.state;

    if (articleUrl && !articleName && !siteName) {
      this.setState({ loading: true, errors: {} });

      const reading = await ContentPageService.getArticleInfo(articleUrl);

      if (reading) {
        this.setState(reading);
      }
      this.setState({ loading: false, errors: {} });
    }
  };

  addArticle = async () => {
    const { t } = this.props;
    let statusCode = HttpCodes.OK;
    const { articleUrl, siteName, articleName, faviconUrl } = this.state;

    try {
      await new ComplementaryReadingValidation(t).validate({
        articleUrl,
        siteName,
        articleName,
        faviconUrl
      });

      this.props.onArticleChange([
        ...this.props.articles,
        { articleUrl, siteName, articleName, faviconUrl }
      ]);

      this.setState({
        articleUrl: '',
        articleName: '',
        siteName: '',
        faviconUrl: '',
        errors: {}
      });
    } catch (error) {
      if (error instanceof ValidationError) {
        this.setState({ errors: error.errors });
      } else {
        UiService.setAlertError('unexpected_error');
      }
      statusCode = HttpCodes.BAD_REQUEST;
    }

    DatahubEventsService.sendEvent({
      schema: new TryCreateUrlReadSchema({
        statusCode,
        result: statusCode === HttpCodes.OK ? 'SUCCESSFUL' : 'FAILURE'
      })
    });
  };

  removeArticle = (index) => {
    const articles = this.props.articles.filter((_, i) => i !== index);

    this.props.onArticleChange(articles);
  };

  render() {
    const { articles, t } = this.props;
    const { articleUrl, siteName, articleName, loading, faviconError, errors } =
      this.state;

    return (
      <div className="complementary__reading">
        <div className="_position-relative">
          <div className="_d-flex _flex-column">
            <div>
              <InputLabel htmlFor="article-url">
                {t('complementary_reading.title')}
              </InputLabel>
              <p className="_text-2 _text-gray-400">
                {t('complementary_reading.description')}
              </p>
              <Input
                id="article-url"
                value={articleUrl}
                onChange={this.urlChange}
                onBlur={this.loadReadingData}
                placeholder={t('complementary_reading.url_placeholder')}
                error={errors.articleUrl?.[0]}
              />
            </div>
            <div>
              <InputLabel htmlFor="site-name">
                {t('complementary_reading.name_placeholder')}
              </InputLabel>
              <Input
                id="site-name"
                value={siteName}
                onChange={this.nameChange}
                placeholder={t('complementary_reading.name_placeholder')}
                error={errors.siteName?.[0]}
              />
            </div>
            <div>
              <InputLabel htmlFor="article-name">
                {t('complementary_reading.article_placeholder')}
              </InputLabel>
              <Input
                id="article-name"
                value={articleName}
                onChange={this.articleChange}
                placeholder={t('complementary_reading.article_placeholder')}
                error={errors.articleName?.[0]}
              />
            </div>
            <Button
              variation="secondary"
              size="block"
              className="_mt-4"
              disabled={!(articleUrl && siteName && articleName)}
              onClick={this.addArticle}
            >
              {t('complementary_reading.add_link')}
            </Button>
          </div>

          {loading && <Loading overlay />}
        </div>
        <section className="_d-flex _flex-wrap _gap-4 _mt-4">
          {articles.map((article, index) => {
            return (
              <Card key={article.id} data-test="reading-card">
                <div className="card__header _d-flex _align-items-center">
                  {article.faviconUrl && !faviconError && (
                    <img
                      className="article__favicon"
                      src={article.faviconUrl}
                      onError={() => this.setState({ faviconError: true })}
                      alt=""
                    />
                  )}
                  <span className="card__title">{article.siteName}</span>
                </div>
                <div className="card__body">
                  <a
                    href={sanitize({
                      html: decodeURIComponent(article.articleUrl)
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="_truncate-3">{article.articleName}</span>
                  </a>
                </div>
                <div className="card__footer">
                  <ButtonIcon
                    className="card__button"
                    transparent
                    type="secondary"
                    icon="delete"
                    role="button"
                    onClick={() => this.removeArticle(index)}
                  >
                    {t('general.remove')}
                  </ButtonIcon>
                </div>
              </Card>
            );
          })}
        </section>
      </div>
    );
  }
}
